import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { IGroupDto, MixpanelService } from "moo-shared";
import { IAuthApiGroupDto } from "../../../core/authentication/authentication-api.service";
import { LoginType } from "../../../core/authentication/authentication-server.service";
import { IIpOrganisationDto } from "../../../core/authentication/ip-organisation.dto";
import { IpTokenService } from "../../../core/authentication/iptoken.service";
import { ResourceApiService } from "../../../core/authentication/resource-api.service";
import { BaseComponent } from "../../../shared/base.component";
import { ButtonTile } from "../../../shared/models/button-tile.model";
import { GroupTile } from "../../../shared/models/group-tile.model";
import { LoginService } from "../../login/login.service";
import { LoginAssistantService } from "../login-assistant.service";

@Component({
  selector: "app-group-list",
  templateUrl: "./group-list.component.html"
})

export class GroupListComponent extends BaseComponent implements OnInit {
  override componentTranslationRoot = "login-assistant.group-list";
  private authApiGroups: IAuthApiGroupDto[] = [];
  private qrLoginTypes = [LoginType.QrCode, LoginType.QrCodeAndPictures];

  organisationName = "";
  groupTiles: GroupTile[] = [];
  backTile = ButtonTile.createBackTile(this.translate("back-to-schools"));
  personnelTile = ButtonTile.createPersonnelTile(this.translate("username-password", {}, "login-assistant.common"));
  qrGroupTile = ButtonTile.createQrTile(this.translate("qr-code-login"));
  hasGroupWithQrLogin = false;
  showBackToSchoolsTile = false;

  constructor(
    translateService: TranslateService,
    private readonly router: Router,
    private readonly loginAssistantService: LoginAssistantService,
    private readonly resourceApiService: ResourceApiService,
    private readonly ipTokenService: IpTokenService,
    private readonly route: ActivatedRoute,
    private readonly loginService: LoginService,
    private readonly mixpanelService: MixpanelService
  ) {
    super(translateService);
  }

  ngOnInit(): void {
    this.showBackToSchoolsTile = this.ipTokenService.hasMultipleOrganisations();

    this.subscriptionsToRelease.push(
      this.route.data.subscribe(({ organisation, groups, authApiGroups }) => {
        this.setOrganisationData(organisation, groups);
        this.authApiGroups = authApiGroups;

        this.hasGroupWithQrLogin = this.authApiGroups.some(x => this.qrLoginTypes.includes(x.LoginType));
      })
    );
  }

  private setOrganisationData(organisation: IIpOrganisationDto, groups: IGroupDto[]) {
    this.organisationName = organisation.Name;
    this.groupTiles = groups.map((group: IGroupDto) => GroupTile.fromDto(group));
    this.groupTiles.forEach(group => {
      group.picture = this.resourceApiService.getGroupPictureUrl(group.key);
    });
    this.groupTiles.sort((a: GroupTile, b: GroupTile) => a.name.localeCompare(b.name));
  }

  goBack(): void {
    if (this.ipTokenService.hasMultipleOrganisations()) {
      this.loginAssistantService.clearGroup();
      this.loginAssistantService.navigateToOrganisationSelection();
    } else {
      this.loginAssistantService.clearOrganisation();
      this.loginAssistantService.navigateToLogin();
    }
  }

  openGroup(group: GroupTile): void {
    const groupHasQrLogin = this.authApiGroups.findIndex(x => x.Key === group.key && this.qrLoginTypes.includes(x.LoginType)) !== -1;
    if (groupHasQrLogin) {
      this.openQrLogin("Group");
      return;
    }

    this.loginAssistantService.selectGroup(group);
    if (group.key === ButtonTile.PersonnelTileKey) {
      this.loginAssistantService.navigateToLogin();
    } else {
      this.router.navigate([group.key], { relativeTo: this.route });
    }
  }

  openPersonnel(): void {
    this.loginAssistantService.selectGroup(this.personnelTile);
    this.loginAssistantService.navigateToLogin();
  }

  openQrLogin(from: string): void {
    this.mixpanelService.track("LoginAssistant.QrLogin", {
      OrganisationKey: this.loginAssistantService.selectedOrganisationKey,
      OpenedVia: from
    });

    this.loginAssistantService.selectGroup(this.qrGroupTile);
    this.loginService.navigateToQrLogin();
  }
}
